import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { CrudSection } from '../components/CrudSection';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { formatCurrency, formatPercent, formatNumber, formatEurPerDay } from '../lib/format';
import { Boxes, Building2, Layers, PlusCircle, CalendarDays, ChevronRight, Info, Pencil, Check, X, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Divisioni principali gestite dalle pagine Mix (sola lettura qui)
const MAIN_DIVISIONS = ['Installatori', 'Impianti'];
// Divisioni disponibili per le altre fonti di ricavo (editabili)
const OTHER_DIVISIONS = [
  { value: 'ELP', label: 'ELP' },
  { value: 'Altro', label: 'Altro' },
];

function DivisionResume({ title, subtitle, icon: Icon, accent, revenue, pctRevenue, priceLabel, priceValue, volumeKwp, margin, daily, details, subtitleList, mixHref, panelShare, navigate, editable, onSaveTotal, saving, volAlert }) {
  const accentStyle = { color: accent.base };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const startEdit = () => { setDraft(String(Math.round(revenue || 0))); setEditing(true); };
  const doSave = async () => {
    const num = parseFloat(String(draft).replace(/\./g, '').replace(',', '.')) || 0;
    const ok = await onSaveTotal(num);
    if (ok) setEditing(false);
  };
  return (
    <div className="space-y-3">
      {/* Card riepilogo divisione */}
      <Card className="p-5 overflow-hidden" style={{ borderColor: accent.border }} data-testid={`division-resume-${title}`}>
        {volAlert && (
          <div className="mb-4 rounded-lg bg-[hsl(var(--destructive)/0.12)] border border-[hsl(var(--destructive)/0.30)] px-3 py-2.5 flex items-start gap-2" data-testid={`division-vol-alert-${title}`}>
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--destructive))] mt-0.5 shrink-0" />
            <span className="text-xs text-foreground leading-relaxed">
              Incongruità con la Rete Vendita: il volume gestito a provvigione (<span className="font-semibold tabular-nums">{formatCurrency(volAlert.vol)}</span>) supera i ricavi impostati per questa divisione di <span className="font-semibold text-[hsl(var(--destructive))] tabular-nums">{formatCurrency(volAlert.delta)}</span>.
            </span>
          </div>
        )}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: accent.tint, color: accent.base }}><Icon className="h-5 w-5" /></span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate">{title}</h3>
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            {editing ? (
              <div className="flex items-center gap-1 justify-end">
                <Input type="number" step="1000" value={draft} onChange={(e) => setDraft(e.target.value)} className="h-9 w-40 text-right tabular-nums" autoFocus data-testid={`division-total-input-${title}`} />
                <Button size="icon" variant="ghost" className="h-9 w-9 text-[hsl(var(--success))]" onClick={doSave} disabled={saving} data-testid={`division-total-save-${title}`}><Check className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground" onClick={() => setEditing(false)} disabled={saving}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 justify-end">
                <div className="text-2xl font-semibold tabular-nums" style={accentStyle} data-testid={`division-total-${title}`}>{formatCurrency(revenue)}</div>
                {editable && (
                  <button type="button" onClick={startEdit} className="text-muted-foreground/70 hover:text-foreground transition-colors" title="Modifica ricavo divisione" aria-label={`Modifica ricavo ${title}`} data-testid={`division-total-edit-${title}`}><Pencil className="h-3.5 w-3.5" /></button>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-0.5">{formatPercent(pctRevenue)} dei ricavi</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground leading-tight">{priceLabel}</div>
            <div className="text-lg font-semibold tabular-nums mt-0.5">{priceValue}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground leading-tight">Volume</div>
            <div className="text-lg font-semibold tabular-nums mt-0.5">{formatNumber(volumeKwp, 1)} kWp</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground leading-tight">Margine ponderato</div>
            <div className="text-lg font-semibold tabular-nums mt-0.5">{formatPercent(margin)}</div>
          </div>
        </div>

        <div className="mt-4 rounded-lg px-4 py-2.5 flex items-center gap-2 border-l-4" style={{ backgroundColor: accent.tint, borderColor: accent.base }}>
          <CalendarDays className="h-4 w-4" style={accentStyle} />
          <span className="text-sm text-foreground">Media giornaliera:</span>
          <span className="text-sm font-bold tabular-nums ml-auto" style={accentStyle}>{formatEurPerDay(daily)}</span>
        </div>

        {panelShare !== undefined && panelShare < 0.999 && (
          <div className="mt-3 rounded-lg bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 px-3 py-2 flex items-start gap-2">
            <Info className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
            <span className="text-xs text-foreground">Solo {formatPercent(panelShare)} dei ricavi sono pannelli FV (usati per il volume kWp).</span>
          </div>
        )}

        <button onClick={() => navigate(mixHref)} className="mt-4 w-full flex items-center justify-end gap-1 text-sm font-medium transition-colors hover:opacity-80" style={accentStyle} data-testid={`open-mix-${title}`}>
          Apri dettaglio mix <ChevronRight className="h-4 w-4" />
        </button>
      </Card>

      {/* Card sotto-categorie · media giornaliera */}
      <Card className="p-5" data-testid={`division-details-${title}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{subtitleList} · Media giornaliera</div>
        <div className="divide-y">
          {(details || []).length === 0 && <div className="py-4 text-sm text-muted-foreground">Nessuna categoria definita.</div>}
          {(details || []).map((d, i) => (
            <div key={d.id || i} className="flex items-center justify-between gap-3 py-2.5" data-testid={`detail-row-${title}-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{d.name}</div>
                <div className="text-xs text-muted-foreground">{formatPercent(d.share_normalized)} · {formatCurrency(d.revenue_derived)}/anno</div>
              </div>
              <div className="text-base font-semibold tabular-nums shrink-0" style={accentStyle}>{formatEurPerDay(d.daily_sales)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function Ricavi() {
  const { active, sid, loading, error, reload } = useScenario();
  const { canWriteList } = useAuth();
  const canWrite = canWriteList('revenues');
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [savingDiv, setSavingDiv] = useState(null); // 'inst' | 'imp' | null

  const loadKpi = useCallback(() => { if (sid) api.getKpi(sid).then(setKpi).catch(() => {}); }, [sid]);
  useEffect(() => { loadKpi(); }, [loadKpi, active]);
  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);

  if (loading) return (<div><PageHeader title="Ricavi" /><LoadingTable /></div>);
  if (error || !active) return (<div><PageHeader title="Ricavi" /><ErrorState onRetry={reload} /></div>);

  const rows = active.revenues || [];
  const others = rows.filter((r) => !MAIN_DIVISIONS.includes(r.division));
  const totalRev = kpi?.revenues_total ?? rows.reduce((s, r) => s + (r.amount || 0), 0);
  const daysYear = kpi?.days_per_year || 365;
  const perDay = (annual) => (annual || 0) / daysYear;
  const pct = (v) => (totalRev ? (v || 0) / totalRev : 0);

  // Voci "revenues" che compongono ciascuna divisione principale (il backend somma Installatori + BtoB)
  const installatoriItems = rows.filter((r) => r.division === 'Installatori' || r.division === 'BtoB');
  const impiantiItems = rows.filter((r) => r.division === 'Impianti');

  // Modifica del SOLO totale divisione: scala proporzionalmente le voci sottostanti (preserva il mix)
  const saveDivisionTotal = async (which, items, newTotal) => {
    if (!items.length || !(newTotal >= 0)) { toast.error('Valore non valido'); return false; }
    setSavingDiv(which);
    try {
      const current = items.reduce((s, r) => s + (r.amount || 0), 0);
      if (current > 0) {
        const factor = newTotal / current;
        await Promise.all(items.map((it) => api.updateItem(sid, 'revenues', it.id, { amount: Math.round((it.amount || 0) * factor) })));
      } else {
        await Promise.all(items.map((it, i) => api.updateItem(sid, 'revenues', it.id, { amount: i === 0 ? Math.round(newTotal) : 0 })));
      }
      toast.success('Ricavo divisione aggiornato');
      reloadAll();
      return true;
    } catch (e) {
      toast.error('Errore nel salvataggio');
      return false;
    } finally { setSavingDiv(null); }
  };

  const GREEN = { base: 'hsl(152,45%,32%)', tint: 'hsla(152,45%,32%,0.10)', border: 'hsla(152,45%,32%,0.35)' };
  const AMBER = { base: 'hsl(32,85%,42%)', tint: 'hsla(32,85%,42%,0.12)', border: 'hsla(32,85%,42%,0.35)' };

  // ---- Incongruità Rete Vendita: volume gestito a provvigione vs ricavi divisione ----
  const agents = active.agents || [];
  const volByDiv = { installatori: 0, impianti: 0, shared: 0 };
  agents.forEach((a) => { if (!a.benefit_disabled) { const d = a.division || 'shared'; volByDiv[d] = (volByDiv[d] || 0) + (a.managed_volume || 0); } });
  const makeVolAlert = (vol, rev) => { const delta = (vol || 0) - (rev || 0); return delta > 0.5 ? { vol: vol || 0, rev: rev || 0, delta } : null; };
  const alertInstallatori = kpi ? makeVolAlert(volByDiv.installatori, kpi.product_mix_revenue) : null;
  const alertImpianti = kpi ? makeVolAlert(volByDiv.impianti, kpi.client_mix_revenue) : null;

  // BLOCCO 2 — Altre fonti di ricavo (editabili)
  const columns = [
    { key: 'name', header: 'Voce di ricavo', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'division', header: 'Divisione', render: (r) => <Badge variant="secondary">{r.division || '—'}</Badge>, sortValue: (r) => r.division || '' },
    { key: 'margin_pct', header: 'Marginalità', align: 'right', render: (r) => formatPercent(r.margin_pct) },
    { key: 'daily', header: 'Vendita/giorno', align: 'right', render: (r) => formatEurPerDay(perDay(r.amount)), sortValue: (r) => r.amount || 0 },
    { key: 'amount', header: 'Ricavo annuo', align: 'right', render: (r) => formatCurrency(r.amount) },
  ];
  const fields = [
    { name: 'name', label: 'Voce di ricavo', type: 'text' },
    { name: 'division', label: 'Divisione', type: 'select', options: OTHER_DIVISIONS },
    { name: 'amount', label: 'Ricavo annuo (€)', type: 'number' },
    { name: 'margin_pct', label: 'Marginalità (%)', type: 'percent' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];
  const othersTotal = others.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-8">
      <PageHeader title="Ricavi" description={`Totale ricavi: ${formatCurrency(totalRev)} · ${formatEurPerDay(kpi?.daily_sales_total)} · riepilogo divisioni e altre fonti`} />
      {!canWrite && <p className="text-xs text-muted-foreground">Modalità sola lettura per il tuo ruolo.</p>}

      {/* BLOCCO 1 — Divisioni principali (resume, sola lettura) */}
      <section className="space-y-3" data-testid="ricavi-block-divisioni">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Divisioni principali</h3>
          <Badge variant="outline" className="text-[10px] font-normal">Totale editabile · dettaglio dalle pagine Mix</Badge>
        </div>
        {kpi ? (
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6">
              <DivisionResume
                title="Divisione Installatori" subtitle="Vendita pannelli FV + cross-selling" icon={Boxes} accent={GREEN}
                revenue={kpi.product_mix_revenue} pctRevenue={pct(kpi.product_mix_revenue)}
                priceLabel="Prezzo medio / Wp" priceValue={`${formatNumber(kpi.price_wp_installatori, 3)} €`}
                volumeKwp={(kpi.vol_wp_installatori || 0) / 1000} margin={kpi.product_mix_weighted_margin_effective}
                daily={kpi.daily_sales_installatori} details={kpi.product_mix_details} subtitleList="Categorie prodotto"
                panelShare={kpi.panel_share_installatori} mixHref="/ricavi/mix-installatori" navigate={navigate}
                editable={canWrite} saving={savingDiv === 'inst'} onSaveTotal={(v) => saveDivisionTotal('inst', installatoriItems, v)} volAlert={alertInstallatori} />
            </div>
            <div className="col-span-12 lg:col-span-6">
              <DivisionResume
                title="Divisione Impianti" subtitle="Impianti chiavi in mano" icon={Building2} accent={AMBER}
                revenue={kpi.client_mix_revenue} pctRevenue={pct(kpi.client_mix_revenue)}
                priceLabel="Prezzo medio / kWp" priceValue={formatCurrency(kpi.price_kwp_impianti)}
                volumeKwp={(kpi.vol_wp_impianti || 0) / 1000} margin={kpi.client_mix_weighted_margin_effective}
                daily={kpi.daily_sales_impianti} details={kpi.client_mix_details} subtitleList="Target cliente"
                mixHref="/ricavi/mix-impianti" navigate={navigate}
                editable={canWrite} saving={savingDiv === 'imp'} onSaveTotal={(v) => saveDivisionTotal('imp', impiantiItems, v)} volAlert={alertImpianti} />
            </div>
          </div>
        ) : <LoadingTable />}
      </section>

      {/* BLOCCO 2 — Altre fonti di ricavo (editabili) */}
      <section className="space-y-3" data-testid="ricavi-block-altre">
        <div className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Altre fonti di ricavo</h3>
          <Badge variant="outline" className="text-[10px] font-normal">Editabile</Badge>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">
          Fonti di ricavo aggiuntive (es. ELP, altre divisioni) con la propria marginalità dedicata. Aggiungi, modifica o elimina le voci.
        </p>
        <CrudSection title={`Altre fonti · ${formatCurrency(othersTotal)}/anno`} rows={others} columns={columns} fields={fields} canWrite={canWrite} addLabel="Nuova fonte di ricavo"
          onAdd={(p) => api.addItem(sid, 'revenues', p)} onUpdate={(id, p) => api.updateItem(sid, 'revenues', id, p)} onDelete={(id) => api.deleteItem(sid, 'revenues', id)}
          afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
      </section>
    </div>
  );
}
